import { useEffect } from 'react';
import { APP_VERSION } from '../version';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    title: string;
    items: string[];
  }[];
}

// Changelog data - update this with each release
export const changelogData: ChangelogEntry[] = [
  {
    version: '1.5.117',
    date: '2026-01-28 04:30 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'New Ticket: Auto-lookup role when assigning tech (fixes "must assign both assignedResourceID and assignedResourceRoleID" error)',
        ],
      },
    ],
  },
  {
    version: '1.5.116',
    date: '2026-01-28 04:00 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Dashboard: Hidden resources now filtered from tech dropdown',
          'Resource Sync: Removed X-Sync-Secret requirement (uses standard auth)',
        ],
      },
    ],
  },
  {
    version: '1.5.115',
    date: '2026-01-28 03:45 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'System Settings: Resource Sync button to sync technicians from AutoTask',
          'Resource sync updates defaultRoleId for time entry role assignment',
        ],
      },
    ],
  },
  {
    version: '1.5.114',
    date: '2026-01-28 03:30 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Admin config API: Fixed 404 error by changing route from /admin/* to /sys/*',
          'Tech User Management: Hide/show toggles now work correctly',
          'Calendar tech dropdown: Hidden resources are now filtered out',
        ],
      },
    ],
  },
  {
    version: '1.5.113',
    date: '2026-01-28 00:15 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'SECURITY: Non-admin users can no longer see admin menu items',
          'Admin check now requires valid email match (no false positives)',
        ],
      },
    ],
  },
  {
    version: '1.5.112',
    date: '2026-01-27 23:40 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Calendar now available to all users (removed from beta)',
          'Time entry role assignment: Now syncs defaultRoleId from AutoTask',
          'Fixed "AssignedResourceID and AssignedRoleID combination not defined" error',
        ],
      },
      {
        title: 'Added',
        items: [
          'Tech User Management: Hide/show techs from Calendar dropdown using toggles',
          'Hidden techs shown with strikethrough and HIDDEN badge',
          'API endpoints for managing hidden resource IDs',
        ],
      },
    ],
  },
  {
    version: '1.5.110',
    date: '2026-01-27 19:00 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Time Entry Edit: Click entry popup Edit button to modify hours, description, bid-job',
          'Time Entry Delete: Delete entries with confirmation dialog',
          'Tech User Management: New settings page to add/edit/remove contractors',
          'Bid-job dropdown now shows in entry popup when assigned',
        ],
      },
      {
        title: 'Changed',
        items: [
          'Removed BETA badge from Calendar tab',
          'Settings menu reorganized: User Settings & Feedback on top, admin items below divider',
          'Edit warning always shows (all entries sync to AutoTask/TimeTech)',
        ],
      },
      {
        title: 'Fixed',
        items: [
          'Reports tab now shows consistently for admins on all pages',
          'Admin settings menu items now show on all pages (was only appearing on Calendar)',
          'Bid-job dropdown: Fixed company ID mismatch causing only "PRJ" to appear',
        ],
      },
    ],
  },
  {
    version: '1.4.33',
    date: '2026-01-18 07:00 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Queue names now properly fetched from AutoTask Queues entity',
        ],
      },
    ],
  },
  {
    version: '1.4.32',
    date: '2026-01-18 06:30 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Queue column on Tickets page with dropdown filter',
          'Queue names now sync from AutoTask (instead of showing "Queue 12345")',
        ],
      },
    ],
  },
  {
    version: '1.4.31',
    date: '2026-01-18 06:00 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Duplicate Check: Settings page now has database diagnostics section',
          'Sort indicators always visible on Tickets page (○ for unsorted, ▲/▼ for sorted)',
        ],
      },
      {
        title: 'Fixed',
        items: [
          'Created filter no longer goes off screen on Tickets page',
          'Assigned filter now shows dropdown with tech names instead of text input',
        ],
      },
    ],
  },
  {
    version: '1.4.30',
    date: '2026-01-18 05:30 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Smaller 7-day chunks for older ranges (60-67, 67-75, 75-82, 82-90)',
          'Auto backfill now uses 8 chunks instead of 6',
        ],
      },
    ],
  },
  {
    version: '1.4.29',
    date: '2026-01-18 05:10 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Auto Backfill 90 Days: One button runs all date ranges automatically',
          'Shows progress as it runs each range',
        ],
      },
    ],
  },
  {
    version: '1.4.28',
    date: '2026-01-18 05:00 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Backfill uses 14-day chunks (0-14, 14-30, 30-45, etc.)',
        ],
      },
    ],
  },
  {
    version: '1.4.27',
    date: '2026-01-18 04:45 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Added delays to backfill to reduce Cosmos DB load (50ms between upserts)',
        ],
      },
    ],
  },
  {
    version: '1.4.26',
    date: '2026-01-18 04:30 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Backfill uses specific date ranges instead of "last X days"',
        ],
      },
    ],
  },
  {
    version: '1.4.21',
    date: '2026-01-18 03:30 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Historical Backfill: Import old tickets from AutoTask (Settings page)',
          'Status/Priority IDs saved for reliable updates',
        ],
      },
    ],
  },
  {
    version: '1.4.20',
    date: '2026-01-18 02:30 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Dashboard: Tech filter dropdown between stat cards',
          'Tickets page: Complete rewrite with column filters',
          'Click ticket # opens modal, click row expands details',
        ],
      },
    ],
  },
  {
    version: '1.4.16',
    date: '2026-01-18 00:10 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Change Status/Priority dropdowns in ticket modal',
          'Reassign tickets to different technicians',
        ],
      },
    ],
  },
  {
    version: '1.4.15',
    date: '2026-01-17 23:45 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Auto-sync when opening ticket modal',
          'Sync after actions (assign, notes, time, complete)',
        ],
      },
    ],
  },
  {
    version: '1.4.14',
    date: '2026-01-17 23:15 UTC',
    sections: [
      {
        title: 'Changed',
        items: [
          'Ticket modal UX redesign: cleaner layout',
          'Assign section only shows when unassigned',
          'Complete ticket checkbox when adding time',
        ],
      },
    ],
  },
  {
    version: '1.3.10',
    date: '2026-01-17 07:04 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Status mapping: Added missing statuses to all sync functions',
        ],
      },
    ],
  },
  {
    version: '1.2.5',
    date: '2026-01-16 21:00 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Column filters now work correctly on all views',
          'Filter dropdowns open upward to avoid being cut off',
        ],
      },
    ],
  },
  {
    version: '1.2.1',
    date: '2026-01-16 18:00 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Client filter now applies immediately when selecting a client',
          'Filter dropdowns no longer cut off when table has few rows',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-01-16 17:45 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Client filter now works correctly - filtering by client updates the table',
          'Client dropdown shows all available clients (not just from filtered results)',
          'Hidden invalid "Company XXX" entries from client dropdown',
        ],
      },
    ],
  },
  {
    version: '1.1.9',
    date: '2026-01-16 17:30 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Client filter: Hide invalid "Company XXX" entries from dropdown (orphaned company IDs)',
        ],
      },
      {
        title: 'Improved',
        items: [
          'Sync logging: Added warnings for tickets with missing company data to help debug data issues',
        ],
      },
    ],
  },
  {
    version: '1.1.8',
    date: '2026-01-16 17:10 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          '"Company XXX" names: Include inactive companies in lookup for correct ticket names',
        ],
      },
    ],
  },
  {
    version: '1.1.7',
    date: '2026-01-16 17:00 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Sorting tip: Hint above table showing how to sort and multi-sort',
        ],
      },
    ],
  },
  {
    version: '1.1.6',
    date: '2026-01-16 16:55 UTC',
    sections: [
      {
        title: 'Fixed',
        items: [
          'Shift+click multi-sort: Fixed event handling for adding multiple sort columns',
        ],
      },
    ],
  },
  {
    version: '1.1.5',
    date: '2026-01-16 16:45 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Multi-column sorting: Shift+click headers to add secondary/tertiary sort',
          'Sort order numbers: Shows 1, 2, 3 badges when sorting by multiple columns',
          'Sort indicators always visible: Shows ○ for unsorted, ▲/▼ for sorted',
        ],
      },
      {
        title: 'Improved',
        items: [
          'Version/changelog styling: Version plain text, "changelog" as clickable link',
        ],
      },
    ],
  },
  {
    version: '1.1.4',
    date: '2026-01-16 16:30 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Column sorting: Click any column header to sort ascending/descending',
          'Sort indicators: Arrows show current sort column and direction',
        ],
      },
      {
        title: 'Improved',
        items: [
          'Client filter: Now shows only clients from displayed tickets, not all clients',
        ],
      },
    ],
  },
  {
    version: '1.1.3',
    date: '2026-01-16 16:15 UTC',
    sections: [
      {
        title: 'Improved',
        items: [
          'Changelog now opens in a styled modal instead of GitHub',
          'Color-coded sections (green for Added, blue for Improved, yellow for Fixed)',
        ],
      },
    ],
  },
  {
    version: '1.1.2',
    date: '2026-01-16 16:05 UTC',
    sections: [
      {
        title: 'Improved',
        items: [
          'Made changelog link more visible (blue, underlined)',
          'Added development tracking documentation',
        ],
      },
    ],
  },
  {
    version: '1.1.1',
    date: '2026-01-16 15:45 UTC',
    sections: [
      {
        title: 'Improved',
        items: [
          'Client Filter: Shows all clients sorted alphabetically',
          'Client Filter: List filters as you type',
          'Client Filter: Keyboard navigation (Arrow Up/Down, Enter, Escape)',
          'Client Filter: Shows result count at bottom',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-01-16 15:00 UTC',
    sections: [
      {
        title: 'Added',
        items: [
          'Quick Sync: Automatic sync every minute for NEW tickets',
          'Full Sync: Companies first, then tickets (ensures names available)',
          'Company Sync: Manual sync for company data only',
          'Settings: Three sync buttons with result displays',
          'Settings: Automatic sync schedule information',
          'Dashboard: Column filters with contextual popups',
          'Dashboard: "All" stat card to show all tickets',
          'Version display with changelog link',
        ],
      },
      {
        title: 'Fixed',
        items: [
          'Data accuracy: Now fetches ALL open tickets regardless of date',
          'Company names: Fixed "Company XXX" showing instead of real names',
          '"Invalid Date" display issue',
        ],
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-01-15',
    sections: [
      {
        title: 'Added',
        items: [
          'Initial release',
          'Dashboard with ticket list and statistics',
          'Tickets page with detailed view',
          'Reports page with date filtering',
          'Settings page with preferences',
          'AutoTask API integration',
          'Cosmos DB storage',
        ],
      },
    ],
  },
];

function getSectionColor(title: string): string {
  switch (title.toLowerCase()) {
    case 'added':
      return 'text-green-400';
    case 'improved':
      return 'text-blue-400';
    case 'fixed':
      return 'text-yellow-400';
    case 'changed':
      return 'text-purple-400';
    case 'removed':
      return 'text-red-400';
    default:
      return 'text-slate-300';
  }
}

function getSectionBadgeColor(title: string): string {
  switch (title.toLowerCase()) {
    case 'added':
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'improved':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'fixed':
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'changed':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'removed':
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    default:
      return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ChangelogModal({ isOpen, onClose }: ChangelogModalProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/80">
          <div>
            <h2 className="text-xl font-bold text-white">Changelog</h2>
            <p className="text-sm text-slate-400">AutoTask Hub v{APP_VERSION}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {changelogData.map((entry, index) => (
            <div key={entry.version} className={index > 0 ? 'pt-6 border-t border-slate-700' : ''}>
              {/* Version header */}
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-lg font-bold ${entry.version === APP_VERSION ? 'text-blue-400' : 'text-white'}`}>
                  v{entry.version}
                </span>
                {entry.version === APP_VERSION && (
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/30">
                    Current
                  </span>
                )}
                <span className="text-xs text-slate-500">{entry.date}</span>
              </div>

              {/* Sections */}
              <div className="space-y-3">
                {entry.sections.map((section) => (
                  <div key={section.title}>
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded border mb-2 ${getSectionBadgeColor(section.title)}`}>
                      {section.title}
                    </span>
                    <ul className="space-y-1 ml-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${getSectionColor(section.title).replace('text-', 'bg-')}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/80">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
